import { createRulesObject, getFormattedLabel, Input, StatusIcon, Tooltip } from 'alurkerja-ui'
import { useUiFromSpecContext } from '../../../EditorContextProvider'
import clsx from 'clsx'
import { useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { addRandomSuffix } from '../../utils/addRandomSuffix.js'
import { Controller } from 'react-hook-form'
import { parseExpressions } from './utils/parseExpressions.js'

export const RenderExpressionInput = (props: any) => {
  const { item, form } = props
  const { language, designState } = useUiFromSpecContext()
  const fieldSpec = item
  const watchAllFields = form.watch()

  // Watch for changes in constraints for re-rendering
  const constraintsSnapshot = useMemo(() => {
    return JSON.stringify(item.constraints || {})
  }, [item.constraints])

  useEffect(() => {
    if (item.defaultValue && !isEmpty(item.defaultValue)) {
      // Set the original expression as the form value
      form.setValue(item.name, item.defaultValue)
    }

    if (!item.name && isEmpty(item.name)) {
      item.name = addRandomSuffix('input') // Update the item with the generated name
    }
  }, [item.defaultValue, item.name])

  // Apply validation rules based on constraints
  useEffect(() => {
    if (item.name) {
      // Unregister the field first to avoid validation conflicts
      form.unregister(item.name, { keepValue: true })

      // Re-register with updated validation rules
      form.register(item.name, {
        required: item?.constraints?.required ? 'This field is required' : false,
        value: item?.defaultValue,
        // Add other constraint validations as needed
      })
    }
  }, [form, item.name, constraintsSnapshot])

  // Process expressions when field value or any watched fields change
  useEffect(() => {
    const currentValue = fieldSpec.formula
    if (currentValue && typeof currentValue === 'string') {
      const parsedValue = parseExpressions(currentValue, watchAllFields)
      // Directly set the value with the parsed expression result
      if (parsedValue !== currentValue && parsedValue !== form.getValues(item.name)) {
        form.setValue(item.name, parsedValue)
      }
    }
  }, [item.name, watchAllFields, fieldSpec.formula, form])

  // Calculate rules object during render to ensure it has the latest constraints
  const rulesObject = useMemo(() => {
    return createRulesObject(fieldSpec)
  }, [fieldSpec, constraintsSnapshot])

  return (
    <div className={clsx('flex flex-col justify-center')}>
      <div
        className={clsx('flex items-center', {
          'justify-start text-left': designState.textAlign === 'left',
          'justify-center text-center': designState.textAlign === 'center',
          'justify-end text-right': designState.textAlign === 'right',
          'justify-stretch text-justify': designState.textAlign === 'justify',
        })}
      >
        <label
          style={{ fontSize: designState.fontSize, color: designState.textColor }} // Apply labelColor here
          className={clsx(`mr-1 font-medium`, {
            "after:content-['*'] after:text-red-400 after:text-sm": fieldSpec?.constraints?.required,
            underline: designState?.textFormat?.includes('underline'),
            '!font-bold': designState?.textFormat?.includes('bold'),
            italic: designState?.textFormat?.includes('italic'),
          })}
          htmlFor={fieldSpec.name}
        >
          {getFormattedLabel(fieldSpec.label, language)}
        </label>
        {fieldSpec?.tooltip?.enable && (
          <Tooltip content={fieldSpec?.tooltip?.message} trigger="hover" className="max-w-sm" placement="auto">
            <StatusIcon type="info" className="text-base" />
          </Tooltip>
        )}
      </div>

      <Controller
        name={fieldSpec.name}
        control={form.control}
        defaultValue={fieldSpec.defaultValue ?? ''}
        rules={rulesObject}
        render={({ field, fieldState }) => {
          return (
            <>
              <Input
                {...field}
                placeholder={fieldSpec.placeholder || getFormattedLabel(fieldSpec.label, language)}
                disabled={fieldSpec.disabled}
              />
              {fieldState.error && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
            </>
          )
        }}
      />
    </div>
  )
}
